import type { ServicePageData } from './types';

const tabletopPos: ServicePageData = {
  slug: 'tabletop-pos',
  eyebrow: 'Display',
  eyebrowTo: '/services/all-display',
  title: 'Get your message noticed at',
  titleAccent: 'eye level, on the table.',
  intro:
    'Table talkers, strut cards, acrylic holders and counter-top stands put your offer right in front of the customer, whether that is a menu, a promotion or a point-of-sale message. All printed and finished in-house.',
  heroImages: [
    '/home/print/table-talkers.jpg',
    '/home/signboards/acrylic-panel.jpg',
    '/home/signboards/foamex-panel.jpg',
    '/home/signboards/correx-panel.jpg',
    '/home/print/posters.jpg',
    '/home/print/product-labels.jpg',
    '/home/print/business-cards.jpg',
    '/home/print/appointment-cards.jpg',
    '/home/services/printing.jpg',
    '/home/print/flyers.jpg',
    '/home/print/art-prints.jpg',
    '/home/print/table-talkers.jpg',
    '/home/signboards/acrylic-panel.jpg',
    '/home/signboards/foamex-panel.jpg',
  ],

  useCases: [
    { title: 'Cafes and restaurants', text: 'Menus, specials and promotions, right where people are already looking.' },
    { title: 'Shop counters', text: 'Point-of-sale stands for offers, loyalty schemes and new products.' },
    { title: 'Receptions', text: 'Acrylic holders for visitor information, sign-in prompts and directions.' },
    { title: 'Exhibitions and events', text: 'Lightweight strut cards and stands that pack flat and set up in seconds.' },
    { title: 'Bars and hospitality', text: 'Drinks lists, event nights and seasonal promotions on the table.' },
  ],

  optionsHeading: 'Choose your stand',
  optionsIntro: 'A range of printed table-talker and holder styles to suit your budget and how often the message changes.',
  options: [
    {
      id: 'table-talkers',
      title: 'Printed table talkers',
      lead: 'A folded card that stands on its own, no holder needed.',
      body: 'Printed both sides on a heavier card stock and creased down the middle so it stands unaided. The cheapest way to get a changing message onto every table.',
      image: '/home/print/table-talkers.jpg',
      specs: [
        { label: 'Size', value: 'A6 or A5 folded, custom sizes on request.' },
        { label: 'Material', value: '350gsm silk or matt card.' },
        { label: 'Finish', value: 'Full colour, both sides.' },
      ],
    },
    {
      id: 'strut-cards',
      title: 'Strut cards',
      lead: 'A rigid printed board with a fold-out leg on the back.',
      body: 'More durable than a folded card and easy to swap out. A good middle ground between a paper table talker and a permanent acrylic holder.',
      image: '/home/signboards/foamex-panel.jpg',
      specs: [
        { label: 'Size', value: 'A6 to A3, custom sizes available.' },
        { label: 'Material', value: 'Foamex or correx with a fixed cardboard strut.' },
        { label: 'Finish', value: 'Full colour print, matt or gloss laminate on request.' },
      ],
    },
    {
      id: 'acrylic-holders',
      title: 'Acrylic menu holders',
      lead: 'A clear frame that lets you swap the printed insert whenever you need to.',
      body: 'The insert is your changing content; the holder itself lasts for years. Popular where the message updates often, such as daily specials or seasonal offers.',
      image: '/home/signboards/acrylic-panel.jpg',
      specs: [
        { label: 'Size', value: 'A6, A5 and A4, portrait or landscape.' },
        { label: 'Material', value: '3 or 5mm clear acrylic, polished edges.' },
        { label: 'Fixings', value: 'Free-standing, single or double-sided insert slot.' },
      ],
    },
    {
      id: 'counter-stands',
      title: 'Counter-top display stands',
      lead: 'A freestanding board or leaflet stand for the till point or reception desk.',
      body: 'Sits flat on a counter without leaning on anything. Suits a printed panel, a small poster or a leaflet dispenser depending on what you need on show.',
      image: '/home/signboards/correx-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to A3, custom sizes on request.' },
        { label: 'Material', value: 'Foamex, correx or acrylic depending on how long it needs to last.' },
        { label: 'Fixings', value: 'Fold-out leg or slot-in base.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Photos, gradients and fine detail printed directly onto card, foamex or an acrylic insert on our in-house wide-format printer.',
    },
    {
      title: 'Cut vinyl',
      text: 'Logos and lettering cut from coloured vinyl for a sharper edge where a simple, long-lasting design is all you need.',
    },
  ],

  gallery: [
    '/home/print/table-talkers.jpg',
    '/home/signboards/acrylic-panel.jpg',
    '/home/signboards/foamex-panel.jpg',
    '/home/signboards/correx-panel.jpg',
    '/home/print/posters.jpg',
    '/home/print/product-labels.jpg',
    '/home/print/business-cards.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Every stand arrives ready to use straight out of the box, with inserts pre-cut to fit.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Wipe acrylic and laminated boards clean with a soft cloth. Avoid abrasive cleaners on printed surfaces.',
    },
    {
      title: 'Turnaround',
      text: 'Production usually takes 5 to 10 working days from approval. Got a deadline? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Can I reorder just the printed insert?',
      a: 'Yes. Once we have your holder size and artwork on file, reprinting the insert alone is quick and does not need a new holder.',
    },
    {
      q: 'Can you match my existing menu or brand colours?',
      a: 'Send over your logo or brand guide and we will match colours as closely as the print process allows.',
    },
  ],

  cta: {
    title: 'Get your free table-top display quote.',
    text: 'Tell us how you want to use it and we will recommend the right material and holder.',
    image: '/home/services/printing.jpg',
  },
};

export default tabletopPos;
